import { Component, type OnDestroy, type OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AccessGuardService, RoleGuardService } from '@odin/odin-authentication';
import {
  Alignment,
  CardHelperService,
  ConfirmationService,
  FormBuilder,
  FormData,
  FormOption,
  FormValidation,
  InputIconType,
  InputPrefixSuffixType,
  InputType,
  MerchantChangeService,
  MerchantService,
  MonekColour,
  NotificationService,
  OptionInputType,
  Size,
  StaticHelpers,
  TestMidCondition,
  VTService,
} from '@odin/odin-core';
import { ReceiptDetail, VTPaymentRequest, VTPaymentResponse, } from '@odin/odin-transactions';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CountryConvertService } from '@odin/odin-core';
import { CurrencyPipe } from "@angular/common";
import paymentAmountFields from '../payment-amount-fields/payment-amount-fields';

enum VTPage {
  payment = 'payment',
  success = 'success',
  failed = 'failed',
}

@Component({
  standalone: false,
  selector: 'odin-virtual-terminal-page',
  templateUrl: './virtual-terminal-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./virtual-terminal-page.component.scss'],
})
export class VirtualTerminalPageComponent implements OnInit, OnDestroy {
  private static readonly MOTO_TYPE_STORAGE_KEY = 'vt-default-moto-type';
  private unsubscribe$: Subject<void> = new Subject<void>();
  public virtualTerminalForm: FormData<VTPaymentRequest> = this.BuildForm();
  public lastRequest: VTPaymentRequest;
  public paymentResponse: VTPaymentResponse;
  public processing = false;
  public page: VTPage = VTPage.payment;
  public retryCount: number = 0;
  public transactionBuidName: string;
  public receiptDetail?: ReceiptDetail;

  public printing: boolean = false;

  private cardInputIcon: InputPrefixSuffixType = new InputPrefixSuffixType(
    InputIconType.ICON,
    'credit_card',
  );

  private merchantChangeSub: Subscription;

  constructor(
    private vtService: VTService,
    private cardService: CardHelperService,
    private confirmationService: ConfirmationService,
    private notificationservice: NotificationService,
    private accessService: AccessGuardService,
    private merchantService: MerchantService,
    private merchantChangeService: MerchantChangeService,
    private countryConvertService: CountryConvertService,
    private currencyPipe: CurrencyPipe,
    private roleGuardService: RoleGuardService,
  ) {
  }

  ngOnInit(): void {
    if (!this.PermissionChecker()) {
      this.accessService.bootNoAccess('Virtual Terminal');
    }

    this.merchantChangeSub =
      this.merchantChangeService.merchantChangeEvent.subscribe(() => {
        if (!this.PermissionChecker()) {
          this.accessService.bootNoAccess('Virtual Terminal');
        } else {
          this.virtualTerminalForm = this.BuildForm();
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.merchantChangeSub !== undefined)
      this.merchantChangeSub.unsubscribe();
  }

  public PermissionChecker(): boolean {
    const hasVtFeature = this.accessService.buidHasFeature('vt');

    return hasVtFeature &&
      (this.accessService.hasNewGrant('txn:Create') || this.roleGuardService.hasPermission('txn:Create'));
  }


  public ResetPage(clearForm: boolean): void {
    if (clearForm || this.retryCount >= 3) {
      this.virtualTerminalForm = this.BuildForm(); // resets ALL data, including idempotency token
      this.retryCount = 0;
      this.lastRequest = new VTPaymentRequest(); // clears data out of memory, card number etc
      this.receiptDetail = undefined;
    }
    this.page = VTPage.payment;
  }

  public RetryPayment(): void {
    if (this.page === VTPage.failed) {
      this.SubmitAction(this.lastRequest, StaticHelpers.uuidv4());
      this.retryCount++;
    }
  }

  private SubmitAction(data: VTPaymentRequest, idempotency?: string): void {
    if (this.processing) return;

    if (!data.CV2 || data.CV2.trim().length === 0) {
      this.confirmationService
        .ConfirmDecline(
          'Process without CV2?',
          'You are about to process a MOTO transaction without a CV2. '
          + 'The card issuer will not be able to verify the security code, '
          + 'which increases the risk of fraud.',
          'Confirm',
          'Decline',
          'primary',
          'warn',
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((confirmed: boolean) => {
          if (confirmed) this.ProcessPayment(data, idempotency);
        });
      return;
    }

    this.ProcessPayment(data, idempotency);
  }

  private ProcessPayment(data: VTPaymentRequest, idempotency?: string): void {
    if (this.processing) return;
    this.processing = true;

    // somehow got past the ui validation, clearing the form
    if (this.retryCount >= 3) {
      this.ResetPage(true);
      return;
    }

    // formatting - strip spaces from the card number (e.g. "4539 7910 0173 0106") before sending to Thor
    data.CardNumber = (data.CardNumber ?? '').replace(/\s+/g, '');
    data.CardExpiryMonth = (data.CardExpiryMonth ?? '').trim();
    // accept YY or YYYY expiry years, normalise to YY
    data.CardExpiryYear = (data.CardExpiryYear ?? '').trim().slice(-2);
    data.CardExpiry = new Date(
      `${data.CardExpiryMonth}/28/20${data.CardExpiryYear}`,
    ).toISOString();

    // AMEX line-item detail: description capped at 15 characters, "Goods" when empty
    const paymentDetail = (data.PaymentDetail ?? '').trim();
    data.LIDItem1Description = paymentDetail ? paymentDetail.substring(0, 15) : 'Goods';
    data.LIDItem1Quantity = 1;
    data.LIDItem1GrossValue = data.Amount;

    // Set OrderId to PaymentReference
    if (data.PaymentReference && data.PaymentReference.length > 0) {
      data.OrderId = data.PaymentReference;
    }

    // used for retries - explicitly reset the idempotency token - sent when "retrying" a payment after a fail - invalid funds, etc
    if (idempotency) data = { ...data, IdempotencyToken: idempotency };

    const hasChanged = JSON.stringify({ ...data, IdempotencyToken: '' }) !== JSON.stringify({
      ...this.lastRequest,
      IdempotencyToken: ''
    });
    if (!hasChanged) {
      // CORRECTLY don't update the idempotency token if the request has not changed
    } else {
      // req has changed - update idempotency token
      data.IdempotencyToken = StaticHelpers.uuidv4();
    }

    this.lastRequest = data;

    if (this.merchantService.getTestMidCondition() === TestMidCondition.Limited) {
      this.notificationservice.AlertDialogWindow(
        "The data entered is valid, but "
        + "the currently selected MID cannot submit a transaction."
      );
      this.processing = false;
      return;
    }

    this.vtService.SubmitPaymentRequest(data)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (resp: VTPaymentResponse) => {
          this.processing = false;
          this.paymentResponse = resp;
          resp.time = new Date();
          this.receiptDetail = undefined;
          if (resp.status) {
            // remove confidential details
            this.lastRequest.CardNumber = '';
            this.lastRequest.CV2 = '';
            const currentBuid = this.merchantService.getCurrentBuid();
            if (currentBuid !== null) {
              this.transactionBuidName = currentBuid.name;
            }
            this.receiptDetail = new ReceiptDetail(
              this.transactionBuidName,
              this.paymentResponse.traceId,
              this.lastRequest.QAName,
              this.lastRequest.PaymentDetail,
              this.paymentResponse.time,
              this.paymentResponse.message,
              this.lastRequest.Amount,
              this.lastRequest.CurrencyCode,
            );
            this.page = VTPage.success;
          } else {
            this.page = VTPage.failed;
          }
        },
        error: (errorResp) => {
          this.receiptDetail = undefined;
          this.processing = false;

          const errorMessage: string = Array.isArray(errorResp?.error.errors) && errorResp.error.errors.length > 0
            ? errorResp.error.errors[0]
            : 'An unknown error occurred while processing the payment.';

          this.notificationservice.ErrorSmallDialog(errorMessage, errorResp);
        },
        complete:
          () => {
            this.processing = false;
          }
      });
  }

  private GetDefaultMotoType(): string {
    const stored = localStorage.getItem(VirtualTerminalPageComponent.MOTO_TYPE_STORAGE_KEY);
    return stored === 'mail' || stored === 'telephone' ? stored : 'telephone';
  }

  private BuildForm(): FormData<VTPaymentRequest> {
    this.cardInputIcon = new InputPrefixSuffixType(
      InputIconType.ICON,
      'credit_card',
    );
    const userProfile = this.merchantService.getUserprofile();
    const currentMid = this.merchantService.getCurrentMid();

    const { amountFieldProperties, currencyFieldProperties } = paymentAmountFields({
      amountFieldName: 'Amount',
      currencyFieldName: 'CurrencyCode',
      midCurrencies: currentMid.currencyCodes,
      countryConvertService: this.countryConvertService,
      currencyPipe: this.currencyPipe,
      onCurrencyChange: (iconType: InputIconType, symbol: string) => {
        if (this.virtualTerminalForm) {
          const amountField = this.virtualTerminalForm.formFields.find(f => f.id === 'Amount');
          if (amountField && amountField.data && amountField.data.prefixIcon) {
            amountField.data.prefixIcon.iconType = iconType;
            amountField.data.prefixIcon.value = symbol;
          }
        }
      }
    });

    const formBuilder = new FormBuilder<VTPaymentRequest>(MonekColour.Primary)
      .ToggleSubmitButton(true)
      .SubmitButtonConfig('Process Payment', Size.Third, Alignment.Left)

      //Hidden Fields
      .AddFormField(
        'OperatorId',
        '',
        InputType.Hidden,
        userProfile?.userId || '',
        Size.Full,
      )
      .AddFormField(
        'OperatorName',
        '',
        InputType.Hidden,
        userProfile?.email || '',
        Size.Full,
      )

      .AddFormField('MerchantID', '', InputType.Hidden, '', Size.Full)
      .AddFormField('CountryCode', '', InputType.Hidden, '826', Size.Full)
      .AddFormField('ResponseAction', '', InputType.Hidden, 'JSON', Size.Full)
      .AddFormField('QAProducts', '', InputType.Hidden, '', Size.Full)
      .AddFormField(
        'IdempotencyToken',
        '',
        InputType.Hidden,
        StaticHelpers.uuidv4(),
        Size.Full,
      )

      //Potential Prompts
      .AddFormField('Dispatch', '', InputType.Hidden, 'NOW', Size.Full)
      .AddOptionField(
        'MotoType',
        'Order Type',
        OptionInputType.Radio,
        this.GetDefaultMotoType(),
        [
          new FormOption('telephone', 'Telephone Order'),
          new FormOption('mail', 'Mail Order'),
        ],
        Size.Full,
        new FormValidation().Required(),
        true,
        {},
        (
          value: string | number | boolean | Date | null,
          data: unknown,
          dataCallback: (data: unknown) => void,
          valCallback: (val: string | number | boolean | Date | null) => void,
        ) => {
          valCallback(value);
          if (value === 'mail' || value === 'telephone') {
            localStorage.setItem(VirtualTerminalPageComponent.MOTO_TYPE_STORAGE_KEY, value);
          }
        },
      );

    if (this.merchantService.getTestMidCondition() === TestMidCondition.Limited) {
      formBuilder.AddSection(
        '-- The currently selected MID cannot submit a transaction, '
        + 'but you can still test this form --'
      );
    }

    const formData = formBuilder
      .AddFormField(...amountFieldProperties)
      .AddOptionField(...currencyFieldProperties)
      .AddFormField(
        'PaymentDetail',
        'Description',
        InputType.Text,
        '',
        Size.Full,
        new FormValidation().MaxLength(
          50,
          true,
          'Detail can not exceed 50 characters',
        ),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'description'),
        },
      )
      .AddFormField(
        'PaymentReference',
        'Order Reference',
        InputType.Text,
        '',
        Size.Full,
        new FormValidation().MaxLength(
          50,
          true,
          'Reference can not exceed 50 characters',
        ),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'sell'),
        },
        undefined,
      )
      .AddFormField(
        'QAName',
        'Cardholder Name',
        InputType.Text,
        '',
        Size.Full,
        new FormValidation().Required().MaxLength(50, true, 'Name can not exceed 50 characters'),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'face'),
        },
      )
      .AddFormField(
        'EmailAddress',
        'Cardholder Email',
        InputType.Email,
        '',
        Size.Full,
        new FormValidation().IsEmail().MaxLength(50),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'alternate_email'),
        },
      )

      .AddFormField(
        'BillingLine1',
        'Billing Address Line 1',
        InputType.Text,
        '',
        Size.TwoThird,
        new FormValidation().MaxLength(50, true, 'Address can not exceed 50 characters'),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'home'),
        },
      )
      .AddFormField(
        'BillingPostcode',
        'Billing Postcode',
        InputType.Text,
        '',
        Size.Third,
        new FormValidation().MaxLength(8, true, 'Postcode can not exceed 8 characters'),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'location_on'),
        },
      )


      .AddFormField(
        'CardNumber',
        'Card Number',
        InputType.Text,
        '',
        Size.Full,
        new FormValidation()
          .PatternMatch(
            '^[0-9]+( ?[0-9]+)*$',
            'Please enter a valid card number using only numbers and spaces',
          )
          .MinLength(8, true, 'Card number must be between 8 - 20 characters')
          .MaxLength(24, true, 'Card number must be 8 - 20 characters')
          .Required(),
        false,
        {
          prefixIcon: this.cardInputIcon,
          class: 'card-font',
        },
        (
          val: string | number | boolean | Date | null,
          data: { prefixIcon: InputPrefixSuffixType; class: string },
          dataCallback: (data: {
            prefixIcon: InputPrefixSuffixType;
            class: string;
          }) => void,
          valCallback: (data: string | number | boolean | Date | null) => void,
        ) => {
          if (typeof val !== 'string') return;
          valCallback(val);
          const cardType = this.cardService.GetCardType(val);
          this.cardInputIcon = this.cardService.GetCardIcon(cardType);
          const updatedData = data;
          updatedData.prefixIcon = this.cardInputIcon;
          dataCallback(updatedData);
        },
      )
      .AddDetail(
        'CV2 is optional for MOTO transactions.',
        'Providing it allows the card issuer to verify the security code and may help reduce fraud. Leaving it blank increases fraud risk.',
      )
      .AddFormField(
        'CV2',
        'CV2',
        InputType.Text,
        '',
        Size.Half,
        new FormValidation().PatternMatch('^[0-9]{3,4}$', 'Invalid CV2'),
        false,
        {
          prefixIcon: new InputPrefixSuffixType(InputIconType.ICON, 'security'),
        },
      )
      .AddFormField(
        'CardExpiryMonth',
        'Card Expiry Month (MM)',
        InputType.Text,
        null,
        Size.Quater,
        new FormValidation().ExactLength(2).MinValue(1).MaxValue(12).Required(),
      )
      .AddFormField(
        'CardExpiryYear',
        'Card Expiry Year (YY)',
        InputType.Text,
        null,
        Size.Quater,
        new FormValidation()
          .PatternMatch('^([0-9]{2}|20[0-9]{2})$', 'Enter the expiry year as YY or YYYY, e.g. 28 or 2028')
          .Required(),
      )
      .AddDetail('Store Card Details', 'If you wish to store card details for future use, please select "Yes".')
      .AddOptionField(
        'AcceptCof',
        'Store Card Details',
        OptionInputType.Checkbox,
        null,
        [
          new FormOption('Y', 'Yes'),
          new FormOption('N', 'No'),
          new FormOption(null, 'N/A', true),
        ],
        Size.Full,
        new FormValidation(),
        true,
      )

      .Build((data) => this.SubmitAction(data));

    return formData;
  }

  public PrintReceipt(): void {
    print();
  }
}

